import type { ComponentType } from "react"
import { action, computed, makeObservable, observable } from "mobx"

import type { IEventBus } from "@/domains/events"
import type { ManagerStore } from "@/domains/manager/manager-store"
import { ManagerIcon } from "@/domains/icons/huge-icons/monotone/manager"

import type { WalletConnectionStore } from "./wallet-connection-store"
import { WalletConnectedEvent } from "./events"
import { getConnectorDisplayName, getConnectorIcon } from "./utils/connector-icon"

export type ButtonVariant = "default" | "manager" | "primary" | "disconnected"

export class WalletMenuStore {
  isMenuOpen = false
  private shouldDeselectManagerOnConnect = false

  constructor(
    private readonly walletStore: WalletConnectionStore,
    private readonly managerStore: ManagerStore,
    private readonly eventBus: IEventBus,
  ) {
    makeObservable(this, {
      isMenuOpen: observable,
      displayText: computed,
      buttonVariant: computed,
      showManagerIcon: computed,
      currentIcon: computed,
      currentConnectorName: computed,
      isConnected: computed,
      isManagerMode: computed,
      openMenu: action,
      closeMenu: action,
      handleConnectClick: action,
      handleDisconnect: action,
      selectManager: action,
      deselectManager: action,
    })

    // Listen for wallet connection events to handle "deselect manager on connect"
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.handleWalletConnected.bind(this))
  }

  // Computed properties
  get displayText(): string {
    // If in manager mode and manager address exists, show manager name or address
    if (this.isManagerMode && this.managerStore.selectedManagerAddress) {
      const managerName = this.managerStore.getManagerAddressName(this.managerStore.selectedManagerAddress)
      return managerName || this.managerStore.selectedManagerAddress.truncate()
    }

    // If connected (not in manager mode), show connected address
    if (this.isConnected && !this.isManagerMode && this.walletStore.connectedAddress) {
      return this.walletStore.connectedAddress.truncate()
    }

    // Otherwise show "Connect"
    return "Connect"
  }

  get buttonVariant(): ButtonVariant {
    if (this.isManagerMode) {
      return "manager-flat"
    }
    if (this.isConnected) {
      return "connected-flat"
    }
    return "default"
  }

  get showManagerIcon(): boolean {
    return this.isManagerMode && this.managerStore.selectedManagerAddress !== null
  }

  get currentIcon(): ComponentType<any> | null {
    // If in manager mode, show manager icon
    if (this.isManagerMode && this.managerStore.selectedManagerAddress !== null) {
      return ManagerIcon
    }

    // If connected, show the wallet connector icon
    if (this.isConnected && this.walletStore.connectorId) {
      return getConnectorIcon({
        id: this.walletStore.connectorId,
        name: this.walletStore.connectorName ?? "",
        type: this.walletStore.connectorType ?? undefined,
      })
    }

    // No icon when disconnected
    return null
  }

  get currentConnectorName(): string | null {
    // If connected, return the connector display name
    if (this.isConnected && this.walletStore.connectorId) {
      return getConnectorDisplayName({
        id: this.walletStore.connectorId,
        name: this.walletStore.connectorName ?? "",
        type: this.walletStore.connectorType ?? undefined,
      })
    }

    // No connector name when disconnected
    return null
  }

  get isConnected(): boolean {
    return this.walletStore.isConnected
  }

  get isManagerMode(): boolean {
    return this.managerStore.isManagerMode
  }

  // Actions
  openMenu(): void {
    this.isMenuOpen = true
  }

  closeMenu(): void {
    this.isMenuOpen = false
  }

  handleConnectClick(): void {
    // If a manager is selected, mark that we should deselect it on successful connection
    if (this.managerStore.selectedManagerAddress) {
      this.shouldDeselectManagerOnConnect = true
    }
  }

  handleDisconnect(): void {
    // Disconnect is handled by wagmi's useDisconnect hook
    // This method is here for consistency, but the actual disconnect
    // should be called from the component using wagmi's hook
  }

  selectManager(address: (typeof this.managerStore.managerAddresses)[0] | null): void {
    if (address === null) {
      // Selecting connected wallet (deselecting manager)
      this.managerStore.setSelectedManagerAddress(null)
    } else {
      const isCurrentlySelected = this.managerStore.selectedManagerAddress?.equals(address) ?? false
      // Toggle: if already selected, deselect (use connected wallet)
      this.managerStore.setSelectedManagerAddress(isCurrentlySelected ? null : address)
    }
  }

  deselectManager(): void {
    this.managerStore.setSelectedManagerAddress(null)
  }

  // Event handlers
  private handleWalletConnected(event: WalletConnectedEvent): void {
    // If we should deselect manager on connect, do it now
    if (this.shouldDeselectManagerOnConnect && this.managerStore.selectedManagerAddress) {
      this.managerStore.setSelectedManagerAddress(null)
      this.shouldDeselectManagerOnConnect = false
    }
  }
}
